"use client";

import { c as _c } from "react/compiler-runtime";
import { TextSelection } from "@tiptap/pm/state";
import { Extensions, JSONContent } from "@tiptap/react";
import posthog from "posthog-js";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { cx } from "@/lib/tailwind-utils";
import type { SuggestionItem } from "@/types/suggestions";
import { AttachmentChip, Attachments } from "./attachments";
import { BlockPropertiesPanel } from "./block-properties-panel";
import { prepareEmailHtml } from "./blocks/html-fallback/prepare-html";
import { sanitizeEmailDocument } from "./blocks/html-fallback/sanitize-document";
import { blockRegistry } from "./blocks/registry";
import { Canvas } from "./canvas";
import { type ActiveBlock, DEFAULT_STYLE_SETTINGS, EmailComposerContext, EmailComposerContextValue, type EmailComposerMode, EmailStyleSettings } from "./context";
import { renderConversationalHtml, renderEmailHtml, renderEmailText } from "./converter/render-to-html";
import { BlockBubbleMenu } from "./editor/block-bubble-menu";
import { ButtonBubbleMenu } from "./editor/button-bubble-menu";
import { useEmailEditor } from "./editor/hooks/use-email-editor";
import { ImageBubbleMenu } from "./editor/image-bubble-menu";
import { LinkBubbleMenu } from "./editor/link-bubble-menu";
import { FloatingToolbar } from "./floating-toolbar";
import { LoadTemplate } from "./load-template";
import { SaveAsTemplate } from "./save-template";
import { Toolbar } from "./toolbar";
import { TiptapEmailJSON } from "./types/tiptap-json";
import { uploadInlineImage } from "@/api/provider/comms/email/email";
export interface EmailComposerProps {
  /** Interaction mode: "simple" = click-to-edit (default), "block" = click-to-select row with drag reorder */
  mode?: EmailComposerMode;
  /** Identifier for this composer instance, used to tag PostHog session recordings. */
  composerId?: string;
  initialContent?: JSONContent;
  onChange?: (content: JSONContent) => void;
  variables?: SuggestionItem[];
  styleSettings?: Partial<EmailStyleSettings>;
  /** File attachment handler. When provided, enables the paperclip toolbar button and non-image file drops. */
  onAddFiles?: ((files: File[]) => {
    added: number;
    errors: string[];
  }) | null;
  /** Override inline image upload. Receives a File and should return a URL string for the editor preview. */
  onImageUpload?: ((file: File) => Promise<string>) | null;
  /** Placeholder text shown in the first empty paragraph (visible even with signature content). */
  placeholder?: string;
  /**
   * When true, inserting a suggestion inserts its resolved content as editable
   * text instead of a mention placeholder.
   *
   * Turn this on only for one-off composers — an email sent once, to a
   * recipient already known at compose time (a chat email thread, the patient
   * compose drawer, sending forms, a patient's treatment plan). Leave it off
   * for anything authored once and sent many times later (templates,
   * sequences, blasts, signatures), where the token has to survive into storage
   * and resolve per recipient at send time.
   */
  resolveOnInsert?: boolean;
  /** Called when the AI button is clicked. When provided, shows the AI button in the toolbar. */
  onAI?: (() => void) | null;
  /**
   * Optional async alt-text generator for images. When provided, the image
   * bubble menu shows an AI button to (re)generate alt text for the selected image.
   */
  onGenerateAltText?: ((src: string) => Promise<string>) | null;
  /** Additional TipTap extensions to register (e.g. slash commands). */
  additionalExtensions?: Extensions;
  /** Called when the active block changes (for per-block properties sidebar). */
  onActiveBlockChange?: (block: ActiveBlock | null) => void;
  /** Initial marketing state — shows the unsubscribe footer decoration from the start. */
  initialIsMarketing?: boolean;
  className?: string;
  children: React.ReactNode;
}
export interface EmailComposerRef {
  getHtml: (variableValues?: Record<string, string>) => Promise<string>;
  /**
   * Lightweight, Gmail-style HTML for conversational emails (reply / forward /
   * 1:1 compose). Use this instead of `getHtml` for those — it omits the
   * marketing scaffold so the message renders consistently across clients.
   */
  getConversationalHtml: (variableValues?: Record<string, string>) => Promise<string>;
  getEditorHtml: () => string;
  getText: (variableValues?: Record<string, string>) => Promise<string>;
  getJSON: () => JSONContent;
  setContent: (content: JSONContent) => void;
  /** Set editor content from an HTML string. */
  setHtml: (html: string) => void;
  clear: () => void;
  focus: () => void;
  /** Access the underlying Tiptap editor instance. */
  getEditor: () => import("@tiptap/react").Editor | null;
  /** Show or hide the unsubscribe footer decoration in the editor. */
  setUnsubscribeFooterVisible: (visible: boolean) => void;
  /** Toggle background selection (for clicks on the outer canvas area). */
  selectBackground: () => void;
}
export const EmailComposer = Object.assign(forwardRef<EmailComposerRef, EmailComposerProps>(function EmailComposer(t0, ref) {
  const $ = _c(72);
  const {
    mode: t1,
    composerId,
    initialContent,
    onChange,
    variables: t2,
    styleSettings: styleOverrides,
    onAddFiles: t3,
    onImageUpload: onImageUploadProp,
    onAI: t4,
    onGenerateAltText: t5,
    placeholder,
    resolveOnInsert,
    additionalExtensions,
    onActiveBlockChange,
    initialIsMarketing,
    className,
    children
  } = t0;
  const mode = t1 === undefined ? "simple" : t1;
  let t6;
  if ($[0] !== t2) {
    t6 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const variables = t6;
  const onAddFiles = t3 === undefined ? null : t3;
  const onAI = t4 === undefined ? null : t4;
  const onGenerateAltText = t5 === undefined ? null : t5;
  const [activeBlock, setActiveBlockRaw] = useState(null);
  const [backgroundSelected, setBackgroundSelectedRaw] = useState(false);
  let t7;
  if ($[2] !== onActiveBlockChange) {
    t7 = block => {
      setActiveBlockRaw(block);
      onActiveBlockChange?.(block);
      if (block) {
        setBackgroundSelectedRaw(false);
      }
    };
    $[2] = onActiveBlockChange;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const setActiveBlock = t7;
  let t8;
  if ($[4] !== onActiveBlockChange) {
    t8 = selected => {
      setBackgroundSelectedRaw(selected);
      if (selected) {
        setActiveBlockRaw(null);
        onActiveBlockChange?.(null);
      }
    };
    $[4] = onActiveBlockChange;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const setBackgroundSelected = t8;
  const bubbleMenuContainerRef = useRef(null);
  let t10;
  let t9;
  if ($[6] !== composerId) {
    t9 = () => {
      if (!composerId) {
        return;
      }
      posthog.startSessionRecording({
        sampling: true
      });
      posthog.capture("email_composer_opened", {
        composerId
      });
    };
    t10 = [composerId];
    $[6] = composerId;
    $[7] = t10;
    $[8] = t9;
  } else {
    t10 = $[7];
    t9 = $[8];
  }
  useEffect(t9, t10);
  let t11;
  if ($[9] !== styleOverrides) {
    t11 = () => ({
      ...DEFAULT_STYLE_SETTINGS,
      ...styleOverrides
    });
    $[9] = styleOverrides;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const [styleSettingsState, setStyleSettingsState] = useState(t11);
  let t12;
  let t13;
  if ($[11] !== styleOverrides) {
    t12 = () => {
      setStyleSettingsState({
        ...DEFAULT_STYLE_SETTINGS,
        ...styleOverrides
      });
    };
    t13 = [styleOverrides];
    $[11] = styleOverrides;
    $[12] = t12;
    $[13] = t13;
  } else {
    t12 = $[12];
    t13 = $[13];
  }
  useEffect(t12, t13);
  const styleSettings = styleSettingsState;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = update => {
      setStyleSettingsState(prev => {
        const partial = typeof update === "function" ? update(prev) : update;
        return {
          ...prev,
          ...partial
        };
      });
    };
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const setStyleSettings = t14;
  const defaultImageUpload = _temp;
  const onImageUploadFn = onImageUploadProp ?? defaultImageUpload;
  let t15;
  if ($[15] !== additionalExtensions || $[16] !== initialContent || $[17] !== initialIsMarketing || $[18] !== mode || $[19] !== onAddFiles || $[20] !== onChange || $[21] !== onImageUploadFn || $[22] !== placeholder || $[23] !== resolveOnInsert || $[24] !== variables) {
    t15 = {
      mode,
      initialContent,
      onChange,
      variables,
      onAddFiles,
      onImageUpload: onImageUploadFn,
      placeholder,
      resolveOnInsert,
      additionalExtensions,
      initialIsMarketing
    };
    $[15] = additionalExtensions;
    $[16] = initialContent;
    $[17] = initialIsMarketing;
    $[18] = mode;
    $[19] = onAddFiles;
    $[20] = onChange;
    $[21] = onImageUploadFn;
    $[22] = placeholder;
    $[23] = resolveOnInsert;
    $[24] = variables;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const editor = useEmailEditor(t15);
  let t16;
  let t17;
  if ($[26] !== activeBlock || $[27] !== setActiveBlock) {
    t16 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape" && activeBlock) {
          setActiveBlock(null);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t17 = [activeBlock, setActiveBlock];
    $[26] = activeBlock;
    $[27] = setActiveBlock;
    $[28] = t16;
    $[29] = t17;
  } else {
    t16 = $[28];
    t17 = $[29];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[30] !== editor) {
    t18 = () => {
      if (!editor) {
        return;
      }
      const handler = () => {
        if (editor.isFocused) {
          setBackgroundSelectedRaw(false);
        }
      };
      editor.on("selectionUpdate", handler);
      return () => {
        editor.off("selectionUpdate", handler);
      };
    };
    t19 = [editor];
    $[30] = editor;
    $[31] = t18;
    $[32] = t19;
  } else {
    t18 = $[31];
    t19 = $[32];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[33] !== activeBlock || $[34] !== editor || $[35] !== setActiveBlock) {
    t20 = () => {
      if (!editor || !activeBlock) {
        return;
      }
      const handler_0 = () => {
        const {
          doc
        } = editor.state;
        if (activeBlock.pos < doc.content.size) {
          const resolved = doc.resolve(activeBlock.pos);
          const nodeAtPos = resolved.nodeAfter;
          if (nodeAtPos && nodeAtPos.type.name === activeBlock.node.type.name) {
            if (nodeAtPos !== activeBlock.node) {
              const def = blockRegistry[nodeAtPos.type.name];
              if (def) {
                setActiveBlockRaw({
                  node: nodeAtPos,
                  pos: activeBlock.pos,
                  definition: def
                });
              }
            }
            return;
          }
        }
        setActiveBlock(null);
      };
      editor.on("update", handler_0);
      return () => {
        editor.off("update", handler_0);
      };
    };
    t21 = [editor, activeBlock, setActiveBlock];
    $[33] = activeBlock;
    $[34] = editor;
    $[35] = setActiveBlock;
    $[36] = t20;
    $[37] = t21;
  } else {
    t20 = $[36];
    t21 = $[37];
  }
  useEffect(t20, t21);
  const pendingContentRef = useRef(null);
  let t22;
  let t23;
  if ($[38] !== editor) {
    t22 = () => {
      if (!editor || pendingContentRef.current === null) {
        return;
      }
      editor.commands.setContent(pendingContentRef.current);
      pendingContentRef.current = null;
    };
    t23 = [editor];
    $[38] = editor;
    $[39] = t22;
    $[40] = t23;
  } else {
    t22 = $[39];
    t23 = $[40];
  }
  useEffect(t22, t23);
  let t24;
  if ($[41] !== editor || $[42] !== setBackgroundSelected || $[43] !== styleSettings) {
    t24 = () => ({
      getHtml: async variableValues => {
        if (!editor) {
          return "";
        }
        const json = editor.getJSON() as TiptapEmailJSON;
        return renderEmailHtml(json, styleSettings, variableValues);
      },
      getConversationalHtml: async variableValues_0 => {
        if (!editor) {
          return "";
        }
        const json_0 = editor.getJSON() as TiptapEmailJSON;
        return renderConversationalHtml(json_0, variableValues_0);
      },
      getEditorHtml: () => {
        if (!editor) {
          return "";
        }
        return editor.getHTML();
      },
      getText: async variableValues_1 => {
        if (!editor) {
          return "";
        }
        const json_1 = editor.getJSON() as TiptapEmailJSON;
        return renderEmailText(json_1, variableValues_1);
      },
      getJSON: () => {
        if (!editor) {
          return {
            type: "doc",
            content: []
          };
        }
        return editor.getJSON();
      },
      setContent: content => {
        const sanitized = sanitizeEmailDocument(content);
        if (editor) {
          editor.commands.setContent(sanitized);
        } else {
          pendingContentRef.current = sanitized;
        }
      },
      setHtml: html => {
        const prepared = prepareEmailHtml(html);
        if (editor) {
          editor.commands.setContent(prepared);
        } else {
          pendingContentRef.current = prepared;
        }
      },
      clear: () => {
        pendingContentRef.current = null;
        editor?.commands.clearContent();
      },
      focus: () => {
        editor?.commands.focus();
      },
      getEditor: () => editor,
      setUnsubscribeFooterVisible: visible => {
        editor?.commands.setUnsubscribeFooterVisible(visible);
      },
      selectBackground: () => {
        if (!editor) {
          return;
        }
        if (editor.state.doc.content.size > 0) {
          const $start = editor.state.doc.resolve(0);
          editor.view.dispatch(editor.state.tr.setSelection(TextSelection.near($start, 1)));
        }
        editor.commands.blur();
        setBackgroundSelected(true);
      }
    });
    $[41] = editor;
    $[42] = setBackgroundSelected;
    $[43] = styleSettings;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== editor || $[46] !== styleSettings) {
    t25 = [editor, styleSettings];
    $[45] = editor;
    $[46] = styleSettings;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  useImperativeHandle(ref, t24, t25);
  const t26 = resolveOnInsert ?? false;
  let t27;
  if ($[48] !== activeBlock || $[49] !== backgroundSelected || $[50] !== editor || $[51] !== mode || $[52] !== onAI || $[53] !== onAddFiles || $[54] !== onGenerateAltText || $[55] !== onImageUploadFn || $[56] !== setActiveBlock || $[57] !== setBackgroundSelected || $[58] !== styleSettings || $[59] !== t26 || $[60] !== variables) {
    t27 = {
      mode,
      editor,
      blockRegistry,
      activeBlock,
      setActiveBlock,
      backgroundSelected,
      setBackgroundSelected,
      styleSettings,
      setStyleSettings,
      variables,
      resolveOnInsert: t26,
      onImageUpload: onImageUploadFn,
      onAddFiles,
      onAI,
      onGenerateAltText,
      bubbleMenuContainerRef
    };
    $[48] = activeBlock;
    $[49] = backgroundSelected;
    $[50] = editor;
    $[51] = mode;
    $[52] = onAI;
    $[53] = onAddFiles;
    $[54] = onGenerateAltText;
    $[55] = onImageUploadFn;
    $[56] = setActiveBlock;
    $[57] = setBackgroundSelected;
    $[58] = styleSettings;
    $[59] = t26;
    $[60] = variables;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  const contextValue = t27;
  let t28;
  if ($[62] !== className) {
    t28 = cx("relative", className);
    $[62] = className;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div ref={bubbleMenuContainerRef} data-email-bubble-container={true} className="[&>:first-child]:z-50" />;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== children || $[66] !== mode || $[67] !== t28) {
    t30 = <div data-email-composer={true} data-composer-mode={mode} className={t28}>{children}{t29}</div>;
    $[65] = children;
    $[66] = mode;
    $[67] = t28;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== contextValue || $[70] !== t30) {
    t31 = <EmailComposerContext.Provider value={contextValue}>{t30}</EmailComposerContext.Provider>;
    $[69] = contextValue;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  return t31;
}), {
  AttachmentChip,
  Attachments,
  BlockBubbleMenu,
  BlockPropertiesPanel,
  ButtonBubbleMenu,
  Canvas,
  ImageBubbleMenu,
  LinkBubbleMenu,
  LoadTemplate,
  SaveAsTemplate,
  Toolbar,
  FloatingToolbar
});
async function _temp(file) {
  const result = await uploadInlineImage(file);
  if (!result?.signedUrl) {
    throw new Error("Failed to upload image");
  }
  return result.signedUrl;
}